import { supabase } from '@/integrations/supabase/client';
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { PackageArraySchema } from '@/lib/schemas';

// This file will contain centralized Supabase RPC calls for package management.

type Package = Tables<'packages'>;
type PackageInsert = TablesInsert<'packages'>;
type PackageUpdate = TablesUpdate<'packages'>;

export const getPackages = async (from = 0, to = 19): Promise<Package[]> => {
    const { data, error } = await supabase
      .from('packages')
      .select('id, sequential_number, tracking, guest_name, entry_date, status, room_number, receiver_name, signature, last_updated_by, is_deleted, deletion_requested_at, observations, history')
      .order('entry_date', { ascending: false })
      .range(from, to);
    
    if (error) throw error;
    
    // Runtime validation with Zod
    const parsedData = PackageArraySchema.safeParse(data);
    if (!parsedData.success) {
      console.error("Zod validation error:", parsedData.error.errors);
      throw new Error("Failed to validate package data from the server.");
    }

    return parsedData.data;
};

export const createPackage = async (pkg: Omit<PackageInsert, 'id' | 'last_updated_by' | 'entry_date' | 'sequential_number' | 'history'>, user: any): Promise<Package> => {
    if (!user) throw new Error("User must be authenticated to create a package");

    const username = user.user_metadata?.username || user.email || user.id;
    const now = new Date().toISOString();
    
    const newPackage: PackageInsert = {
      ...pkg,
      guest_name: pkg.guest_name?.trim() || "",
      tracking: pkg.tracking?.trim() || "",
      last_updated_by: username,
      entry_date: now,
      history: [
        { status: "Created", date: now, updatedBy: username },
        { status: "Pending", date: now, updatedBy: username }
      ]
    };

    const { data, error } = await supabase
      .from('packages')
      .insert(newPackage)
      .select()
      .single();
      
    if (error) throw error;
    return data;
};

export const updatePackage = async ({ id, changes }: { id: string, changes: PackageUpdate }): Promise<Package> => {
    const { data, error } = await supabase.from('packages').update(changes).eq('id', id).select().single();
    if (error) throw error;
    return data;
};

export const deletePackage = async (id: string) => {
    const { error } = await supabase.from('packages').delete().eq('id', id);
    if (error) throw error;
    return id;
};

export const getPackageHistory = async (packageId: number) => {
    const { data, error } = await supabase
        .from('package_history')
        .select('*')
        .eq('package_id', packageId)
        .order('timestamp', { ascending: true });
    if (error) throw error;
    return data;
}; 